/**
 * NewBookingModal — 3-step wizard.
 *   1. Contact — find or create a CRM contact (name/email/phone/social).
 *   2. Service & Slot — pick service + resource + date/time slot.
 *   3. Custom Fields — vertical-specific intake (allergies, breed, etc.).
 *
 * On submit, POSTs to /api/booking/bookings which upserts CRM contact
 * (STANDING ORDER) and creates the booking + fires confirmation.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Search, User, Calendar, Check, ArrowLeft, ArrowRight, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Badge } from '../ui/badge';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

export default function NewBookingModal({ open, onClose, onCreated, services, resources }) {
  const [step, setStep] = useState(1);
  const [contactMode, setContactMode] = useState('new'); // 'new' | 'existing'
  const [contact, setContact] = useState({ contact_name: '', email: '', phone: '' });
  const [existingResults, setExistingResults] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [contactSearch, setContactSearch] = useState('');
  const [serviceId, setServiceId] = useState('');
  const [resourceId, setResourceId] = useState('');
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [fieldDefs, setFieldDefs] = useState([]);
  const [customFields, setCustomFields] = useState({});
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      axios.get(`${API}/api/booking/fields`, auth()).then((r) => setFieldDefs(r.data || [])).catch(() => {});
    }
  }, [open]);

  // Load slots when service/date/resource changes
  useEffect(() => {
    if (step !== 2 || !serviceId || !date) return;
    setLoadingSlots(true);
    const params = new URLSearchParams({ date, service_id: serviceId });
    if (resourceId) params.set('resource_id', resourceId);
    axios.get(`${API}/api/booking/availability?${params}`, auth())
      .then((r) => setSlots(r.data.resources || []))
      .catch(() => setSlots([]))
      .finally(() => setLoadingSlots(false));
  }, [step, serviceId, resourceId, date]);

  // Contact search
  useEffect(() => {
    if (contactMode !== 'existing' || !contactSearch) { setExistingResults([]); return; }
    const t = setTimeout(() => {
      axios.get(`${API}/api/clients?search=${encodeURIComponent(contactSearch)}&limit=10`, auth())
        .then((r) => setExistingResults(r.data || []))
        .catch(() => setExistingResults([]));
    }, 300);
    return () => clearTimeout(t);
  }, [contactMode, contactSearch]);

  const canNextStep1 = contactMode === 'existing'
    ? !!selectedContact
    : !!(contact.contact_name && (contact.email || contact.phone));
  const canNextStep2 = !!selectedSlot;

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        service_id: serviceId,
        resource_id: selectedSlot.resource_id,
        start_at: selectedSlot.start,
        notes,
        custom_fields: customFields,
      };
      if (contactMode === 'existing') {
        payload.crm_contact_id = selectedContact.id || selectedContact._id;
      } else {
        payload.contact = contact;
      }
      await axios.post(`${API}/api/booking/bookings`, payload, auth());
      toast.success('Booking created — confirmation email queued.');
      onCreated?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Booking failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="new-booking-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {step === 1 && <><User className="h-5 w-5" /> Step 1 · Who&apos;s the booking for?</>}
            {step === 2 && <><Calendar className="h-5 w-5" /> Step 2 · Service &amp; time</>}
            {step === 3 && <><Check className="h-5 w-5" /> Step 3 · Intake details</>}
          </DialogTitle>
        </DialogHeader>

        {/* Step indicator */}
        <div className="flex items-center gap-2 mb-4">
          {[1, 2, 3].map((n) => (
            <div key={n} className={`h-1.5 flex-1 rounded-full ${n <= step ? 'bg-emerald-brand' : 'bg-gray-200'}`} />
          ))}
        </div>

        {/* Step 1 */}
        {step === 1 && (
          <div className="space-y-4">
            <div className="flex gap-2">
              <Button
                variant={contactMode === 'new' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setContactMode('new')}
                className={contactMode === 'new' ? 'bg-emerald-brand' : ''}
                data-testid="contact-mode-new"
              >New Contact</Button>
              <Button
                variant={contactMode === 'existing' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setContactMode('existing')}
                className={contactMode === 'existing' ? 'bg-emerald-brand' : ''}
                data-testid="contact-mode-existing"
              >Existing Contact</Button>
            </div>

            {contactMode === 'new' && (
              <>
                <div><Label>Full Name *</Label><Input value={contact.contact_name} onChange={(e) => setContact({ ...contact, contact_name: e.target.value })} data-testid="new-contact-name" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Email</Label><Input type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} data-testid="new-contact-email" /></div>
                  <div><Label>Phone</Label><Input value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} data-testid="new-contact-phone" /></div>
                </div>
                <p className="text-xs text-gray-medium">Contact will auto-sync to CRM. Email or phone required.</p>
              </>
            )}

            {contactMode === 'existing' && (
              <>
                <div className="relative">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input className="pl-10" placeholder="Search by name, email, or phone…" value={contactSearch} onChange={(e) => setContactSearch(e.target.value)} data-testid="contact-search" />
                </div>
                {existingResults.length > 0 && (
                  <div className="border rounded-lg divide-y max-h-64 overflow-y-auto">
                    {existingResults.map((c) => {
                      const cid = c.id || c._id;
                      const selectedId = selectedContact?.id || selectedContact?._id;
                      return (
                        <button
                          key={cid}
                          className={`w-full text-left p-3 hover:bg-gray-50 ${cid === selectedId ? 'bg-emerald-50' : ''}`}
                          onClick={() => setSelectedContact(c)}
                          data-testid={`contact-result-${cid}`}
                        >
                          <div className="font-medium">{c.contact_name}</div>
                          <div className="text-xs text-gray-medium">{c.email || c.phone}</div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Step 2 */}
        {step === 2 && (
          <div className="space-y-4">
            <div>
              <Label>Service *</Label>
              <Select value={serviceId} onValueChange={setServiceId}>
                <SelectTrigger data-testid="booking-service"><SelectValue placeholder="Choose service…" /></SelectTrigger>
                <SelectContent>
                  {services.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name} · {s.duration_min} min</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Date *</Label>
                <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="booking-date" />
              </div>
              <div>
                <Label>Resource (optional)</Label>
                <Select value={resourceId || 'any'} onValueChange={(v) => setResourceId(v === 'any' ? '' : v)}>
                  <SelectTrigger><SelectValue placeholder="Any available" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any available</SelectItem>
                    {resources.map((r) => <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {serviceId && (
              <div>
                <Label className="mb-2 block">Available Slots</Label>
                {loadingSlots ? (
                  <div className="flex items-center gap-2 text-sm text-gray-medium"><Loader2 className="h-4 w-4 animate-spin" /> Loading slots…</div>
                ) : (
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {slots.map((r) => (
                      <div key={r.resource_id}>
                        <div className="text-xs font-medium text-gray-500 mb-1">{r.resource_label}</div>
                        <div className="flex flex-wrap gap-1.5">
                          {r.slots.length === 0 ? (
                            <span className="text-xs text-gray-400">No open slots</span>
                          ) : r.slots.map((slot) => {
                            const t = new Date(slot.start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                            const active = selectedSlot?.start === slot.start && selectedSlot?.resource_id === r.resource_id;
                            return (
                              <button
                                key={slot.start}
                                className={`px-3 py-1.5 rounded-md text-xs border transition-colors ${active ? 'bg-emerald-brand text-white border-emerald-brand' : 'bg-white border-gray-200 hover:border-emerald-400'}`}
                                onClick={() => setSelectedSlot({ ...slot, resource_id: r.resource_id })}
                                data-testid={`slot-${r.resource_id}-${slot.start}`}
                              >{t}</button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Step 3 */}
        {step === 3 && (
          <div className="space-y-4">
            {fieldDefs.length === 0 ? (
              <p className="text-sm text-gray-medium">No custom fields configured. Skip to confirm.</p>
            ) : fieldDefs.map((f) => (
              <FieldInput
                key={f.id}
                field={f}
                value={customFields[f.key]}
                onChange={(v) => setCustomFields({ ...customFields, [f.key]: v })}
              />
            ))}
            <div>
              <Label>Internal notes (optional)</Label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} data-testid="booking-notes" />
            </div>
          </div>
        )}

        <DialogFooter className="mt-6 flex flex-row justify-between">
          <Button variant="outline" onClick={step === 1 ? onClose : () => setStep(step - 1)} disabled={submitting}>
            {step === 1 ? 'Cancel' : (<><ArrowLeft className="h-4 w-4 mr-1" /> Back</>)}
          </Button>
          {step < 3 ? (
            <Button
              onClick={() => setStep(step + 1)}
              disabled={(step === 1 && !canNextStep1) || (step === 2 && !canNextStep2)}
              className="bg-emerald-brand"
              data-testid="wizard-next-btn"
            >Next <ArrowRight className="h-4 w-4 ml-1" /></Button>
          ) : (
            <Button onClick={submit} disabled={submitting} className="bg-emerald-brand" data-testid="wizard-submit-btn">
              {submitting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Check className="h-4 w-4 mr-1" />}
              Confirm Booking
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function FieldInput({ field, value, onChange }) {
  const req = field.required && <span className="text-red-500 ml-0.5">*</span>;
  const label = <Label>{field.label}{req}</Label>;
  const tid = `field-${field.key}`;

  if (field.type === 'textarea') {
    return <div>{label}<Textarea value={value || ''} onChange={(e) => onChange(e.target.value)} data-testid={tid} /></div>;
  }
  if (field.type === 'number') {
    return <div>{label}<Input type="number" value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={field.unit ? `in ${field.unit}` : ''} data-testid={tid} /></div>;
  }
  if (field.type === 'date') {
    return <div>{label}<Input type="date" value={value || ''} onChange={(e) => onChange(e.target.value)} data-testid={tid} /></div>;
  }
  if (field.type === 'boolean') {
    return (
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} data-testid={tid} />
        {field.label}
      </label>
    );
  }
  if (field.type === 'select') {
    return (
      <div>{label}
        <Select value={value || ''} onValueChange={onChange}>
          <SelectTrigger data-testid={tid}><SelectValue placeholder="Choose…" /></SelectTrigger>
          <SelectContent>
            {(field.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    );
  }
  if (field.type === 'multiselect') {
    const arr = Array.isArray(value) ? value : [];
    const toggle = (o) => {
      const next = arr.includes(o) ? arr.filter((x) => x !== o) : [...arr, o];
      onChange(next);
    };
    return (
      <div>{label}
        <div className="flex flex-wrap gap-1.5 mt-1">
          {(field.options || []).map((o) => (
            <button
              key={o}
              type="button"
              onClick={() => toggle(o)}
              className={`text-xs px-3 py-1 rounded-full border ${arr.includes(o) ? 'bg-emerald-brand text-white border-emerald-brand' : 'bg-white border-gray-200 hover:border-gray-400'}`}
            >{o}</button>
          ))}
        </div>
      </div>
    );
  }
  if (field.type === 'file') {
    return <div>{label}<Input type="file" accept={field.accept || '*'} onChange={(e) => onChange(e.target.files?.[0]?.name)} data-testid={tid} /></div>;
  }
  // default: text
  return <div>{label}<Input value={value || ''} onChange={(e) => onChange(e.target.value)} data-testid={tid} /></div>;
}
